
import 'whatwg-fetch';
import React, { useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';

/* @intl-code-template-end */
import App from './components/App';
import createFetch from './createFetch';
import configureStore from './store/configureStore';
import history from './history';
import createApolloClient from './core/createApolloClient';
import router from './router';
import { getIntl } from './actions/intl';
import { updateMeta } from './DOMUtils';
import { shouldRenderNewHomepage } from './helpers/newHomepageHostGuard';

/* @intl-code-template addLocaleData(${lang}); */
/* @intl-code-template-end */

// Universal HTTP client
const fetch = createFetch(window.fetch, {
  baseUrl: window.App.apiUrl,
});

const apolloClient = createApolloClient();

// Initialize a new Redux store
// http://redux.js.org/docs/basics/UsageWithReact.html
const store = configureStore(window.App.state, {
  apolloClient,
  fetch,
  history,
});

// Enables critical path CSS rendering
// https://github.com/kriasoft/isomorphic-style-loader
const insertCss = (...styles) => {
  // eslint-disable-next-line no-underscore-dangle
  const removeCss = styles.map(x => x._insertCss());
  return () => {
    removeCss.forEach(f => f());
  };
};

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // For react-apollo
  client: apolloClient,
  store,
  storeSubscription: null,
  // Universal HTTP client
  fetch,
  // intl instance as it can be get with injectIntl
  intl: store.dispatch(getIntl()),
  // LARGE PROJECTS FLAG sourced from the SSR-seeded Redux runtime (server.js read
  // the DB once and dispatched it). Client-side navigations read this plain
  // boolean so route guards never import the server-only DB helper. Fail-closed.
  largeProjectsEnabled:
    (store.getState().runtime && store.getState().runtime.largeProjectsEnabled) ||
    false,
  // NEW HOMEPAGE (home-v2) switch from the SSR-seeded runtime (server.js already applied the
  // setting + prod-host guard). Re-checked against the browser host as a second guard.
  newHomepageEnabled: shouldRenderNewHomepage(
    (store.getState().runtime && store.getState().runtime.newHomepageEnabled) === true,
    window.location.hostname,
  ),
};

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance, reactRoot, reactHydrate;

const scrollPositionsHistory = {};

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  context.intl = store.dispatch(getIntl());

  const isInitialRender = !action;
  try {
    let locale = store.getState().intl.locale
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);
    context.locale = locale;

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);
    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    const AppCallback = () => {
      useEffect(() => {
        // Switch off the native scroll restoration behavior and handle it manually
        // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
        if (window.history && 'scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual';
        }

        const elem = document.getElementById('css');
        if (elem) elem.parentNode.removeChild(elem);
        return;
      })
      return <App locale={locale} context={context} insertCss={insertCss}>{route.component}</App>;
    }

    if (isInitialRender) {
      appInstance = hydrateRoot(container, <AppCallback />)
      reactHydrate = appInstance;
    } else {
      appInstance = reactHydrate?.render(<App locale={locale} context={context} insertCss={insertCss}>{route.component}</App>);
    }

    document.title = route.title;

    updateMeta('description', route.description);

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    window.scrollTo(scrollX, scrollY);

  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('RSK will reload your page after error');
      window.location.reload();
    }
  }
}

let isHistoryObserved = false;
export default function main() {
  // Handle client-side navigation by using HTML5 History API
  // For more information visit https://github.com/mjackson/history#readme
  currentLocation = history.location;
  if (!isHistoryObserved) {
    isHistoryObserved = true;
    history.listen(({ location, action }) => onLocationChange(location, action));
  }
  onLocationChange(currentLocation);
}

// globally accesible entry point
window.RSK_ENTRY = main;

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }
    onLocationChange(currentLocation);
  });
}

// CheckForTheme
let theme = store.getState().currency.theme;
if (__DEV__) {
  if (theme == 'dark') {
    document.body.classList.add('darkMode');
    document.body.classList.remove('lightMode');
  } else {
    document.body.classList.add('lightMode');
    document.body.classList.remove('darkMode');
  }
}
